import { computed, type Ref } from "vue";
import { buildHreflangLinks } from "~/utils/locales/buildHreflangLinks";

/**
 * Emits canonical + hreflang (incl. x-default) from a per-ISO URL map.
 * While emitting, flips `suppressI18nHead` so app.vue drops i18n's own
 * alternates. The flag is reset on cleanup (unmount or map going empty).
 *
 * @param urlByIso - Reactive { iso: "url-path" } without locale prefix.
 *                   Undefined/empty disables output.
 */
export const useLocalizedCanonical = (
	urlByIso: Ref<Record<string, string> | undefined>
) => {
	const { locales, defaultLocale, locale: currentLocale } = useI18n();
	const hreflangByCode = useRuntimeConfig().public.hreflangByCode as Record<
		string,
		string
	>;
	const siteUrl = useSiteBaseUrl();
	const localePath = useLocalePath();

	useHead(
		computed(() => {
			const map = urlByIso.value;
			if (!map || Object.keys(map).length === 0) return {};

			const links: Array<{
				rel: string;
				href: string;
				hreflang?: string;
				key: string;
			}> = [];

			const hrefByLocaleCode: Record<string, string> = {};
			for (const locale of locales.value) {
				const url = map[locale.iso as string];
				if (!url) continue;
				const path = url.startsWith("/") ? url : `/${url}`;
				hrefByLocaleCode[locale.code] =
					`${siteUrl}${localePath(path, locale.code)}`;
			}

			const canonicalHref = hrefByLocaleCode[currentLocale.value];
			if (canonicalHref) {
				links.push({
					key: "i18n-can",
					rel: "canonical",
					href: canonicalHref
				});
			}

			links.push(
				...buildHreflangLinks({
					locales: locales.value,
					hreflangByCode,
					defaultLocale,
					hrefFor: (code) => hrefByLocaleCode[code]
				})
			);

			return { link: links };
		})
	);
};
